import { useEffect, useState } from "react";
import { setAppBadge } from "@/lib/appBadge";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Bell, UserPlus, Flame, Info, Check, CheckCheck, Mail, MessageCircleQuestion, Users2, Cake, Briefcase, HandHelping, Sunrise, Sparkles, ClipboardCheck, ListChecks, GraduationCap, FileUp, UserCheck, UserMinus, Activity, AlertTriangle, LifeBuoy, HeartHandshake, Award, BookOpen, Megaphone, Heart, Smartphone, Wrench, ScrollText, CreditCard, UserCog, Trash2, ShieldAlert } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useLocation } from "wouter";
import type { NotificationKind } from "@shared/schema";

interface NotificationRow {
  id: string;
  recipientUserId: string;
  kind: NotificationKind;
  payload: Record<string, unknown>;
  readAt: string | null;
  createdAt: string;
}

const COPY: Record<NotificationKind, { title: string; body: string; icon: typeof Bell; route?: string }> = {
  relationship_request: {
    title: "New connection request",
    body: "Someone wants to connect with you. Open Settings to review.",
    icon: UserPlus,
    route: "/settings",
  },
  burning_desire: {
    title: "Someone needs immediate support",
    body: "A member of your support network has sent an urgent SOS.",
    icon: Flame,
    route: "/",
  },
  system: {
    title: "ForgeRestore update",
    body: "You have a new update.",
    icon: Info,
  },
  contact_request: {
    title: "New sponsor inquiry",
    body: "Someone would like to connect with you about sponsorship.",
    icon: Mail,
    route: "/sponsor-inquiries",
  },
  contact_request_decline: {
    title: "New message",
    body: "You have a new message in your Messages dashboard.",
    icon: Info,
    route: "/messages",
  },
  contact_info_request: {
    title: "New message",
    body: "A sponsor sent you a message in your Messages dashboard.",
    icon: MessageCircleQuestion,
    route: "/messages",
  },
  contact_message: {
    title: "New message",
    body: "You have a new message in your Messages dashboard.",
    icon: Mail,
    route: "/messages",
  },
  inner_circle_invite: {
    title: "You've been invited to an Inner Circle",
    body: "Open the Inner Circle to accept or decline.",
    icon: Users2,
    route: "/inner-circle",
  },
  inner_circle_post: {
    title: "New post in an Inner Circle",
    body: "Someone shared a new update in a circle you're part of.",
    icon: Users2,
    route: "/inner-circle",
  },
  inner_circle_accept: {
    title: "Inner Circle update",
    body: "Someone joined your Inner Circle.",
    icon: Users2,
    route: "/inner-circle",
  },
  inner_circle_decline: {
    title: "Inner Circle update",
    body: "An invitation update is available.",
    icon: Users2,
    route: "/inner-circle",
  },
  anniversary_upcoming: {
    title: "Anniversary tomorrow",
    // Generic fallback; renderBody() below enriches with the milestone name
    // from the payload (no identifier — milestone alone is anonymity-safe).
    body: "Someone in your recovery circle is celebrating a sobriety anniversary tomorrow.",
    icon: Cake,
    route: "/anniversaries",
  },
  service_meeting_reminder: {
    title: "Service commitment tomorrow",
    body: "You have a service commitment at a meeting tomorrow.",
    icon: Briefcase,
    route: "/service",
  },
  service_term_ending: {
    title: "Service term ending soon",
    body: "One of your service positions is wrapping up.",
    icon: Briefcase,
    route: "/service",
  },
  service_sub_needed: {
    title: "A home-group member needs a sub",
    body: "Someone in your home group is looking for coverage on a service commitment.",
    icon: HandHelping,
    route: "/service",
  },
  service_sub_claimed: {
    title: "Your sub request was claimed",
    body: "A member of your home group offered to cover your service commitment.",
    icon: HandHelping,
    route: "/service",
  },
  daily_checkin_reminder: {
    title: "Daily check-in",
    body: "Open ForgeRestore when you have a moment for today's check-in.",
    icon: Sunrise,
    route: "/",
  },
  peer_encouragement: {
    title: "Someone cheered you on",
    body: "A member of your home group is celebrating with you on ForgeRestore.",
    icon: Sparkles,
    route: "/community-portal",
  },
  crisis_plan_yearly_review: {
    title: "Time to review your Crisis Plans",
    body: "It's been a year since you reviewed a Cravings Crisis Plan. Open ForgeRestore to refresh it.",
    icon: LifeBuoy,
    route: "/triggers-cravings",
  },

  // ── Task #253 — expanded matrix ────────────────────────────────────────────
  // A. Stepwork & sponsorship
  stepwork_approved: {
    title: "Stepwork approved",
    body: "Your sponsor approved stepwork you submitted.",
    icon: ClipboardCheck,
    route: "/",
  },
  assignment_updated: {
    title: "A task was updated",
    body: "Your sponsor updated a task assigned to you.",
    icon: ListChecks,
    route: "/",
  },
  graduation_congrats: {
    title: "Congratulations",
    body: "You've completed Step 12.",
    icon: GraduationCap,
    route: "/",
  },
  stepwork_document_uploaded: {
    title: "New stepwork document",
    body: "A sponsee uploaded a stepwork document for you to review.",
    icon: FileUp,
    route: "/",
  },
  // B. Connection lifecycle
  connection_approved: {
    title: "Connection approved",
    body: "A connection request you sent was approved.",
    icon: UserCheck,
    route: "/settings",
  },
  connection_ended: {
    title: "A connection ended",
    body: "One of your connections has ended.",
    icon: UserMinus,
    route: "/settings",
  },
  // C. Triggers, cravings & crisis tools
  sponsee_craving_logged: {
    title: "A sponsee logged a craving",
    body: "Someone you support logged a craving.",
    icon: Activity,
    route: "/",
  },
  sponsee_trigger_logged: {
    title: "A sponsee logged a trigger",
    body: "Someone you support logged a trigger.",
    icon: AlertTriangle,
    route: "/",
  },
  sponsee_worked_crisis_plan: {
    title: "A sponsee used a Crisis Plan",
    body: "Someone you support worked through a Cravings Crisis Plan.",
    icon: LifeBuoy,
    route: "/",
  },
  sos_next_day_checkback: {
    title: "Checking in",
    body: "Open ForgeRestore when you have a moment to check in with yourself.",
    icon: HeartHandshake,
    route: "/playbook",
  },
  sos_supporter_responding: {
    title: "Help is on the way",
    body: "A supporter is responding to your SOS. You're not alone.",
    icon: HeartHandshake,
    route: "/",
  },
  // D. Personal milestones & self-nudges
  sobriety_milestone: {
    title: "A milestone today",
    body: "You've reached a sobriety milestone.",
    icon: Award,
    route: "/anniversaries",
  },
  journaling_streak: {
    title: "Journaling streak",
    body: "You've kept up a journaling streak. Nice work.",
    icon: BookOpen,
    route: "/journal",
  },
  journaling_gap_nudge: {
    title: "Time to journal?",
    body: "It's been a little while since your last journal entry.",
    icon: BookOpen,
    route: "/journal",
  },
  daily_reading_ready: {
    title: "Today's reading is ready",
    body: "A new daily reading is waiting for you.",
    icon: BookOpen,
    route: "/",
  },
  indicator_checkin_nudge: {
    title: "Your check-in is worth a look",
    body: "One of your check-in signals reached \u201cTime to check in.\u201d",
    icon: Activity,
    route: "/",
  },
  // E. Home group & community
  group_holla: {
    title: "A shout-out for you",
    body: "There's a holla for you in your home group.",
    icon: Megaphone,
    route: "/community-portal",
  },
  member_milestone: {
    title: "A milestone in your circle",
    body: "Someone in your recovery circle reached a sobriety milestone.",
    icon: Award,
    route: "/anniversaries",
  },
  gratitude_updated: {
    title: "Gratitude shared",
    body: "A shared gratitude list was updated.",
    icon: Heart,
    route: "/community-portal",
  },
  service_opportunity: {
    title: "New service opportunity",
    body: "A new service opportunity opened in your home group.",
    icon: Briefcase,
    route: "/service",
  },
  home_group_announcement: {
    title: "New home-group announcement",
    body: "An announcement was posted to your home group.",
    icon: Megaphone,
    route: "/community-portal",
  },
  home_group_new_member: {
    title: "A new home-group member",
    body: "Someone new joined your home group.",
    icon: UserPlus,
    route: "/community-portal",
  },
  // F. Security & account
  device_enrolled: {
    title: "New device enrolled",
    body: "A new device was set up for quick unlock on your account.",
    icon: Smartphone,
    route: "/settings",
  },
  // G. App-wide / admin broadcast
  feature_announcement: {
    title: "What's new in ForgeRestore",
    body: "There's a new feature update.",
    icon: Megaphone,
    route: "/",
  },
  maintenance_notice: {
    title: "Planned maintenance",
    body: "ForgeRestore has planned maintenance coming up.",
    icon: Wrench,
    route: "/",
  },
  policy_update: {
    title: "Policy update",
    body: "Our Privacy Policy or Terms have been updated.",
    icon: ScrollText,
    route: "/settings",
  },
  // G. Support inbox (Contact Us) — self only
  contact_reply: {
    title: "New reply from support",
    body: "The ForgeRestore team replied to your support request.",
    icon: LifeBuoy,
    route: "/contact",
  },
  contact_status_change: {
    title: "Support request update",
    body: "There's an update on your support request.",
    icon: LifeBuoy,
    route: "/contact",
  },
  // H. Monetization (dormant)
  billing_event: {
    title: "Billing update",
    body: "There's an update related to your account.",
    icon: CreditCard,
    route: "/settings",
  },
  access_changed: {
    title: "Account access updated",
    body: "Your account access was updated.",
    icon: UserCog,
    route: "/settings",
  },
  // I. Moderation — self only. Copy is intentionally generic; the warning
  // reason is moderation metadata and never crosses into the notification.
  moderation_warning: {
    title: "A note from the ForgeRestore team",
    body: "The ForgeRestore team has shared a note about your account.",
    icon: ShieldAlert,
    route: "/settings",
  },
};

/**
 * Per-kind body customizer. Returns enriched copy if the payload carries
 * anonymity-safe context we can surface (e.g. milestone name for an
 * anniversary). Falls back to the static COPY.body when nothing extra is
 * available. NEVER read identifiers (first name, eight-digit id) here —
 * push payloads strip those and the in-app view should match.
 */
function renderBody(n: NotificationRow): string {
  const meta = COPY[n.kind] ?? COPY.system;
  if (n.kind === "anniversary_upcoming") {
    const ms = n.payload?.milestoneName;
    if (typeof ms === "string" && ms.length > 0) {
      return `Tomorrow a member of your recovery circle reaches their ${ms}.`;
    }
  }
  if (n.kind === "service_meeting_reminder") {
    const r = n.payload?.roleLabel;
    if (typeof r === "string" && r.length > 0) {
      return `Tomorrow you're serving as ${r} at your meeting.`;
    }
  }
  if (n.kind === "service_term_ending") {
    const days = n.payload?.daysUntil;
    const r = n.payload?.roleLabel;
    if (typeof days === "number" && typeof r === "string") {
      if (days <= 0) return `Your ${r} term ends today. Open ForgeRestore to rotate out or extend.`;
      if (days === 1) return `Your ${r} term ends tomorrow.`;
      return `Your ${r} term ends in ${days} days.`;
    }
  }
  if (n.kind === "service_sub_needed") {
    const r = n.payload?.roleLabel;
    if (typeof r === "string" && r.length > 0) {
      return `A home-group member needs a sub for ${r} at an upcoming meeting.`;
    }
  }
  if (n.kind === "maintenance_notice" || n.kind === "policy_update") {
    // App-wide announcement: a superadmin may include a short, non-identifying
    // message shown in-app only (never sent to a device). Falls back to the
    // generic per-kind copy.
    const m = (n.payload as Record<string, unknown> | undefined)?.message;
    if (typeof m === "string" && m.trim().length > 0) return m.trim();
  }
  if (n.kind === "service_sub_claimed") {
    const r = n.payload?.roleLabel;
    const name = n.payload?.claimerFirstName;
    const eid = n.payload?.claimerEightDigitId;
    const who = typeof name === "string" && name.length > 0
      ? `${name}${typeof eid === "string" && eid.length > 0 ? ` (#${eid})` : ""}`
      : "Someone in your home group";
    if (typeof r === "string" && r.length > 0) {
      return `${who} is covering your ${r} commitment.`;
    }
    return `${who} is covering your service commitment.`;
  }
  return meta.body;
}

interface NotificationPrefsLite {
  inAppEnabled: boolean;
  pushEnabled: boolean;
}

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [, setLocation] = useLocation();

  // Respect the user's in-app toggle. While loading, default to enabled
  // so the bell doesn't flicker on every page load.
  const { data: prefs } = useQuery<NotificationPrefsLite>({
    queryKey: ["/api/notification-preferences"],
    staleTime: 60_000,
  });
  const inAppEnabled = prefs?.inAppEnabled !== false;

  const { data: unreadData } = useQuery<{ count: number; badgeCount?: number }>({
    queryKey: ["/api/notifications/unread-count"],
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    enabled: inAppEnabled,
  });
  const unreadCount = unreadData?.count ?? 0;

  // Task #319 — keep the installed PWA's app-icon badge in sync with the
  // unread badge-eligible count (system/maintenance/policy kinds excluded on
  // the server). Clears/decrements as the user reads notifications; no-op on
  // browsers without the Badging API.
  useEffect(() => {
    if (!unreadData) return;
    void setAppBadge(unreadData.badgeCount ?? 0);
  }, [unreadData]);

  const { data: notifications, isLoading } = useQuery<NotificationRow[]>({
    queryKey: ["/api/notifications"],
    enabled: open && inAppEnabled,
    refetchOnWindowFocus: true,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => apiRequest("POST", "/api/notifications/read-all"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const clearAll = useMutation({
    mutationFn: () => apiRequest("DELETE", "/api/notifications"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
      setConfirmClearOpen(false);
    },
  });

  const handleClick = (n: NotificationRow) => {
    if (!n.readAt) markRead.mutate(n.id);

    // Prefer payload-driven deep-links so notifications land on the actual
    // entity (assignment / step). Fall back to the static per-kind route.
    // - `assignmentId` (e.g. system / new_assignment) → /assignments/:id
    // - `stepId`        (e.g. system / submitted_for_review) → /steps/:id
    const payload = (n.payload ?? {}) as Record<string, unknown>;
    const assignmentId = typeof payload.assignmentId === "string" ? payload.assignmentId : null;
    const stepIdRaw = payload.stepId;
    const stepId =
      typeof stepIdRaw === "number"
        ? stepIdRaw
        : typeof stepIdRaw === "string" && stepIdRaw.length > 0
        ? stepIdRaw
        : null;

    let target: string | null = null;
    if (assignmentId) {
      target = `/assignments/${assignmentId}`;
    } else if (stepId !== null) {
      target = `/steps/${stepId}`;
    } else {
      const meta = COPY[n.kind] ?? COPY.system;
      target = meta.route ?? null;
    }

    if (target) {
      setLocation(target);
      setOpen(false);
    }
  };

  // User has disabled in-app notifications — don't render the bell at all.
  // They can re-enable from Settings → Notifications.
  if (!inAppEnabled) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="button-notification-center"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center text-[10px]"
              data-testid="badge-unread-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[85%] sm:max-w-md flex flex-col pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
          </SheetTitle>
          <SheetDescription>
            Recent activity from your support network.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending || unreadCount === 0}
            data-testid="button-mark-all-read"
          >
            <CheckCheck className="w-4 h-4 mr-2" />
            Mark all read
          </Button>
          <AlertDialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={clearAll.isPending || !notifications || notifications.length === 0}
                data-testid="button-clear-all"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear all
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="dialog-clear-all">
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all notifications?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all of your notifications, both read and
                  unread. This can&apos;t be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-clear-all-cancel">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => clearAll.mutate()}
                  disabled={clearAll.isPending}
                  data-testid="button-clear-all-confirm"
                >
                  Delete all
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <ScrollArea className="flex-1 -mx-6 px-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          ) : !notifications || notifications.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No notifications yet.</p>
              <p className="text-sm text-muted-foreground mt-1">
                You&apos;ll see connection requests and SOS alerts here.
              </p>
            </div>
          ) : (
            <ul className="space-y-2 py-2" data-testid="list-notifications">
              {notifications.map((n) => {
                const meta = COPY[n.kind] ?? COPY.system;
                const Icon = meta.icon;
                const unread = !n.readAt;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleClick(n)}
                      className={`w-full text-left flex gap-3 p-3 rounded-md hover-elevate active-elevate-2 ${
                        unread ? "bg-accent/30" : ""
                      }`}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <div className="shrink-0 mt-0.5">
                        <Icon className={`w-5 h-5 ${unread ? "text-primary" : "text-muted-foreground"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${unread ? "font-medium" : "font-normal"}`}>
                          {meta.title}
                        </p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {renderBody(n)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {n.createdAt
                            ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })
                            : ""}
                        </p>
                      </div>
                      {unread && (
                        <div className="shrink-0 self-start">
                          <span className="block w-2 h-2 rounded-full bg-primary" aria-label="Unread" />
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
